import { countriesInfo, tournamentCountriesInfo } from '@/models/country';

/** `flag-icons` 4×3 ratio — keep in sync with `Flag.tsx`. */
const FLAG_ASPECT_RATIO = 4 / 3;
const CHAKRA_SPACE_UNIT_PX = 4;

let textMeasureCanvas: HTMLCanvasElement | null = null;

function measureTextWidthPx(text: string, fontSizePx: number, fontWeight = 600): number {
  const fallback = text.length * fontSizePx * 0.55;
  if (typeof document === 'undefined') return fallback;

  textMeasureCanvas ??= document.createElement('canvas');
  const ctx = textMeasureCanvas.getContext('2d');
  if (!ctx) return fallback;

  ctx.font = `${fontWeight} ${fontSizePx}px "Open Sans", sans-serif`;
  return ctx.measureText(text).width;
}

function longestTournamentCountryName(): string {
  let longest = '';
  for (const cr of Object.values(tournamentCountriesInfo)) {
    const name = countriesInfo[cr.id].name;
    if (name.length > longest.length) longest = name;
  }
  return longest;
}

function chakraSpacingToPx(spacing: number): number {
  return spacing * CHAKRA_SPACE_UNIT_PX;
}

/** Column counts that tile `teamCount` items into a full rectangle (no partial last row). */
export function columnDivisors(teamCount: number, maxColumns?: number): number[] {
  const divisors: number[] = [];
  for (let cols = 1; cols <= teamCount; cols++) {
    if (teamCount % cols !== 0) continue;
    if (maxColumns != null && cols > maxColumns) continue;
    divisors.push(cols);
  }
  return divisors;
}

/** Minimum card width so the longest tournament label fits on one line. */
export function minSelectionCardWidthPx(options: {
  labelFontSizePx: number;
  flagBoxFill: number;
  cardPy?: number;
  cardPx?: number;
  cardGap?: number;
  minCardHeightPx?: number;
}): number {
  const cardPy = options.cardPy ?? 1.5;
  const cardPx = options.cardPx ?? 2;
  const cardGap = options.cardGap ?? 2;
  const minCardHeightPx = options.minCardHeightPx ?? 48;

  const verticalPadPx = cardPy * CHAKRA_SPACE_UNIT_PX * 2;
  const flagMinPx = options.flagBoxFill < 0.5 ? 8 : 12;
  const flagHeightPx = Math.max(
    flagMinPx,
    Math.round((minCardHeightPx - verticalPadPx) * options.flagBoxFill),
  );
  const flagWidthPx = flagHeightPx * FLAG_ASPECT_RATIO;
  const horizontalPadPx = cardPx * CHAKRA_SPACE_UNIT_PX * 2;
  const gapPx = cardGap * CHAKRA_SPACE_UNIT_PX;
  const textPx = measureTextWidthPx(longestTournamentCountryName(), options.labelFontSizePx);

  return Math.ceil(flagWidthPx + gapPx + horizontalPadPx + textPx + 4);
}

/**
 * Pick the most columns (from valid divisors of `teamCount`) that still leave each card
 * wide enough for a single-line label — always produces a full rectangular grid.
 */
export function selectionColumnsForWidth(
  containerWidthPx: number,
  gapChakra: number,
  minCardWidthPx: number,
  teamCount: number,
  maxColumns?: number,
): number {
  if (containerWidthPx <= 0 || teamCount <= 0) return 1;

  const gapPx = chakraSpacingToPx(gapChakra);
  const candidates = columnDivisors(teamCount, maxColumns).sort((a, b) => b - a);

  for (const cols of candidates) {
    const cellWidth = (containerWidthPx - (cols - 1) * gapPx) / cols;
    if (cellWidth >= minCardWidthPx) return cols;
  }

  return 1;
}
